import { forwardRef } from 'react';
import { tv } from 'tailwind-variants';

/**
 * Binary switch track colors (D27).
 * OFF must read as inactive (muted track) — never a bright fill that looks "on".
 * ON uses success green so true/false stays recognizable when brand primary is monochrome (dark zinc).
 */
const switchTrack = tv({
  base: [
    'relative h-[1.55rem] w-[2.85rem] shrink-0 rounded-full',
    'transition-[background-color,box-shadow] duration-150',
    /* OFF: dim inactive — theme-aware mix, not hardcoded #cbd5e1 (washes out as "on" on charcoal). */
    'bg-[color-mix(in_srgb,var(--color-an-text-muted)_42%,var(--color-an-surface))]',
    'shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-an-border)_70%,transparent)]',
  ].join(' '),
  variants: {
    checked: {
      true: [
        'bg-[var(--color-an-success)]',
        'shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-an-success)_55%,transparent)]',
      ].join(' '),
      false: '',
    },
  },
  defaultVariants: { checked: false },
});

const switchKnob = tv({
  base: [
    'absolute left-[2px] top-[2px] h-5 w-5 rounded-full bg-white',
    'shadow-[0_1px_3px_rgba(15,23,42,0.45)] transition-transform duration-150',
  ].join(' '),
  variants: {
    checked: {
      true: 'translate-x-[1.3rem]',
      false: '',
    },
  },
  defaultVariants: { checked: false },
});

export type SwitchProps = {
  readonly checked: boolean;
  readonly className?: string;
};

/**
 * Visual switch track (pair with a labeled control).
 * D27: Switch = binary on/off only. Prefer Checkbox for independent multi-select;
 * Radio for mutually exclusive choice among 2–5 options.
 */
export const Switch = forwardRef<HTMLSpanElement, SwitchProps>(
  ({ checked, className }, ref) => (
    <span ref={ref} className={switchTrack({ checked, className })} aria-hidden="true">
      <span className={switchKnob({ checked })} />
    </span>
  ),
);
Switch.displayName = 'Switch';
